'use es6';
/*
 * Imports user settings to CodeMirror instance.
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from '../util/miniUnderscore.js';
import debounce from 'react-utils/debounce';
import getComponentName from 'react-utils/getComponentName';
import 'codemirror/addon/comment/continuecomment';
import 'codemirror/addon/display/rulers';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/closetag';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/matchtags';
import 'codemirror/addon/edit/trailingspace';
import 'codemirror/addon/fold/xml-fold';
import Settings from 'react-codemirror/model/Settings';
import { fetchSettings, fetchSettingsFromLocalStorage } from 'react-codemirror/util/UserSettings';
import { loadReactCodeMirrorCSS } from 'react-codemirror/util/LoadCSS';
import loadCSS from 'react-codemirror/decorator/_internal/loadCSS';
import softTabs from 'react-codemirror/decorator/softTabs';
import themes from 'react-codemirror/decorator/themes';
const withLoadCSS = loadCSS([loadReactCodeMirrorCSS('/sass/trailingspace.css')]);
export default function userSettings() {
  return function withUserSettings(CodeMirrorComponent) {
    /*
     * Bundle needed decorators
     */
    const DecoratedCodeMirror = compose(themes(), softTabs())(CodeMirrorComponent);
    /**
     * @class
     */

    class WithUserSettings extends Component {
      constructor(props) {
        super(props);

        this.onFetch = result => {
          const {
            error,
            settings
          } = result;
          const {
            onUserSettingsFetchSuccess,
            onUserSettingsFetchError
          } = this.props;

          if (error && onUserSettingsFetchError) {
            onUserSettingsFetchError(result);
          }

          if (settings) {
            if (onUserSettingsFetchSuccess) {
              onUserSettingsFetchSuccess(result);
            }
          }
        };

        this.onSettingsChangeUpdateState = debounce(({
          model
        }) => {
          if (!this.settings) return;
          this.setState({
            settingProps: model.componentProps
          });
        }, 100);

        this.onSettingsChange = changeObj => {
          if (!this.props.onUserSettingsChange) {
            return;
          }

          const {
            field,
            value,
            oldValue,
            model: settings
          } = changeObj;
          this.props.onUserSettingsChange({
            field,
            value,
            oldValue,
            settings
          });
        };

        this.settings = Settings.getByUserId(props.userId);
        fetchSettingsFromLocalStorage(props.userId);
        this.state = {
          settingProps: this.settings.componentProps
        };
      }

      componentDidMount() {
        if (this.state.settingProps !== this.settings.componentProps) {
          this.setState({
            settingProps: this.settings.componentProps
          });
        }

        this.settings.on(this.onSettingsChangeUpdateState);
        this.settings.on(this.onSettingsChange);

        if (!this.props.disableUserSettings) {
          this.fetchSettings();
        }
      }

      componentDidUpdate(prevProps) {
        const {
          disableUserSettings,
          userId
        } = this.props;

        if (!disableUserSettings && (prevProps.disableUserSettings || prevProps.userId !== userId)) {
          this.fetchSettings();
        }
      }

      componentWillUnmount() {
        if (this.settings) {
          const settings = this.settings;
          this.settings = null;
          settings.off(this.onSettingsChangeUpdateState);
          settings.off(this.onSettingsChange);
        }
      }

      fetchSettings() {
        fetchSettings({
          onSuccess: this.onFetch,
          onError: this.onFetch,
          userId: this.props.userId
        });
      }

      render() {
        const settingProps = this.props.disableUserSettings ? {} : this.state.settingProps;
        return /*#__PURE__*/_jsx(DecoratedCodeMirror, Object.assign({}, settingProps, {}, this.props));
      }

    }
    /*
     * Statics
     */


    WithUserSettings.propTypes = {
      userId: PropTypes.number.isRequired,
      disableUserSettings: PropTypes.bool,
      onUserSettingsFetchSuccess: PropTypes.func,
      onUserSettingsFetchError: PropTypes.func,
      onUserSettingsChange: PropTypes.func
    };
    WithUserSettings.defaultProps = {
      disableUserSettings: false
    };
    WithUserSettings.displayName = `WithUserSettings(${getComponentName(CodeMirrorComponent)})`;
    return compose(withLoadCSS)(WithUserSettings);
  };
}